import {announce} from '@github-ui/aria-live'
import {Button, Checkbox, CheckboxGroup, Dialog, FormControl, Textarea} from '@primer/react'
import {useEffect, useRef, useState, type FormEventHandler, type RefObject} from 'react'
import type {FeedbackOptions, FeedbackSubmitHandler, FeedbackType} from './types'

interface FeedbackDetailsDialogProps {
  commentId: string
  feedbackType: FeedbackType
  onClose: () => void
  onSubmit: FeedbackSubmitHandler
  detailsOnly?: boolean
  feedbackOptions?: FeedbackOptions | null
  feedbackChoiceLabel?: string | null
  title?: string
  subtitle?: string
  textResponseLabel?: string
  allowTextOnly?: boolean
  returnFocusRef?: RefObject<HTMLElement | null>
  initialTextResponse?: string
  textResponseRequired?: boolean
  submitting?: boolean
  submitError?: string
}

export const FeedbackDetailsDialog: React.FC<FeedbackDetailsDialogProps> = ({
  commentId,
  feedbackType,
  onClose,
  onSubmit,
  detailsOnly = false,
  feedbackOptions,
  feedbackChoiceLabel,
  title = 'Provide additional feedback',
  subtitle = 'Please help us improve GitHub Copilot by sharing more details about this feedback.',
  textResponseLabel = 'Share more details about your feedback',
  allowTextOnly = false,
  returnFocusRef,
  initialTextResponse,
  textResponseRequired = false,
  submitting = false,
  submitError,
}) => {
  const [dirty, setDirty] = useState(false)
  const [feedbackChoice, setFeedbackChoice] = useState<string[]>([])
  const [textResponse, setTextResponse] = useState(initialTextResponse ?? '')
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const options = feedbackOptions ?? []
  const showFeedbackOptions = options.length > 0

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- the async save error arrives as a prop after the submit handler has returned.
    if (!submitError || !textareaRef.current) return
    textareaRef.current.focus()
    // A document-level announcement may not be read while the modal is open, so announce from inside it.
    announce(submitError, {assertive: true, element: textareaRef.current})
  }, [submitError])

  const trimmedTextResponse = textResponse.trim()
  const textCanSatisfyRequiredFeedback = allowTextOnly || !showFeedbackOptions
  const missingRequiredFeedback = textCanSatisfyRequiredFeedback
    ? feedbackChoice.length === 0 && trimmedTextResponse.length === 0
    : feedbackChoice.length === 0
  const hasValidationError = dirty && missingRequiredFeedback
  let validationMessage = 'Please enter feedback.'
  if (showFeedbackOptions) {
    validationMessage = allowTextOnly
      ? 'Please select a feedback category or enter feedback.'
      : 'Please select a feedback category.'
  }
  const textResponseError =
    (allowTextOnly || !showFeedbackOptions) && hasValidationError ? validationMessage : submitError

  const handleSubmit: FormEventHandler = e => {
    e.preventDefault()
    // Primer's `loading` only removes the button's click handler; it doesn't stop native form submission.
    if (submitting) return
    setDirty(true)

    if (missingRequiredFeedback) return

    onSubmit({
      commentId,
      type: feedbackType,
      feedbackChoice,
      textResponse: trimmedTextResponse,
      detailsOnly,
    })
  }

  return (
    <Dialog
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      returnFocusRef={returnFocusRef}
      renderBody={() => (
        <form onSubmit={handleSubmit}>
          <Dialog.Body>
            {showFeedbackOptions && (
              <div className="tmp-mb-3">
                <CheckboxGroup onChange={setFeedbackChoice} required={!allowTextOnly}>
                  <CheckboxGroup.Label>{feedbackChoiceLabel ?? 'Category'}</CheckboxGroup.Label>
                  {options.map(opt => (
                    <FormControl key={opt.value} id={`feedback_choice_${opt.value}`}>
                      <Checkbox value={opt.value} />
                      <FormControl.Label>{opt.label}</FormControl.Label>
                    </FormControl>
                  ))}
                  {!allowTextOnly && hasValidationError && (
                    <CheckboxGroup.Validation variant="error">{validationMessage}</CheckboxGroup.Validation>
                  )}
                </CheckboxGroup>
              </div>
            )}
            <FormControl id="text_response" required={textResponseRequired}>
              <FormControl.Label requiredText={textResponseRequired ? '(required)' : undefined}>
                {textResponseLabel}
              </FormControl.Label>
              <Textarea
                ref={textareaRef}
                block
                rows={2}
                wrap="wrap"
                name="text_response"
                value={textResponse}
                readOnly={submitting}
                resize="vertical"
                onChange={e => setTextResponse(e.currentTarget.value)}
              />
              {textResponseError && (
                <FormControl.Validation variant="error">{textResponseError}</FormControl.Validation>
              )}
            </FormControl>
          </Dialog.Body>
          <Dialog.Footer>
            <Button type="submit" disabled={hasValidationError} loading={submitting}>
              Submit
            </Button>
          </Dialog.Footer>
        </form>
      )}
    />
  )
}
